import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getContextualError, logError } from "../../utils/errorHandling";
import { scrollToError } from "../../utils/scrollToError";
import PublicHeader from "../common/PublicHeader";
import authStyles from "./AuthForm.module.css";
import styles from "./Login.module.css";

const Login: React.FC = () => {
  const { signInWithEmail, sendPasswordReset, authError } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [resetEmail, setResetEmail] = useState<string>("");
  const [resetLoading, setResetLoading] = useState<boolean>(false);
  const [resetError, setResetError] = useState<string>("");
  const [resetSuccess, setResetSuccess] = useState<boolean>(false);
  const displayedError = error || authError;

  const openForgotPassword = (): void => {
    setResetEmail("");
    setResetError("");
    setResetSuccess(false);
    setShowForgotPassword(true);
  };

  const handleForgotPasswordSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setResetLoading(true);
    setResetError("");

    if (!resetEmail) {
      setResetError("Please enter your email address");
      setResetLoading(false);
      scrollToError(`.${authStyles["error-message"]}`);
      return;
    }

    try {
      await sendPasswordReset(resetEmail);
      setResetSuccess(true);
    } catch (error) {
      logError(error, "client_auth", "Forgot password");
      setResetError(
        getContextualError(
          error,
          undefined,
          "Failed to send reset email. Please try again.",
        ),
      );
      scrollToError(`.${authStyles["error-message"]}`);
    }

    setResetLoading(false);
  };

  const handleEmailSignIn = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!email || !password) {
      setError("Please enter both email and password");
      setLoading(false);
      // Auto-scroll to error message
      scrollToError(`.${authStyles["error-message"]}`);
      return;
    }

    try {
      await signInWithEmail(email, password);
    } catch (error) {
      logError(error, "emailSignIn");
      setError(getContextualError(error, "login"));
      setLoading(false);
      // Auto-scroll to error message
      scrollToError(`.${authStyles["error-message"]}`);
    }
  };

  return (
    <div className={styles["login-container"]}>
      <PublicHeader />
      <div className={styles["login-content"]}>
        <div className={styles["login-card"]}>
          <h1>HallHero</h1>
          <p>Enter your email and password</p>

          {displayedError && (
            <div className={authStyles["error-message"]}>{displayedError}</div>
          )}

          {showForgotPassword ? (
            <div className={styles["login-form"]}>
              {resetSuccess ? (
                <div className={authStyles["auth-success-message"]}>
                  <p>
                    If an account exists for that email, we've sent a password
                    reset link. Check your inbox.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleForgotPasswordSubmit}
                  className={styles["login-form"]}
                >
                  {resetError && (
                    <div className={authStyles["error-message"]}>
                      {resetError}
                    </div>
                  )}

                  <div className={styles["form-group"]}>
                    <label htmlFor="reset-email">Email address</label>
                    <input
                      id="reset-email"
                      type="email"
                      value={resetEmail}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setResetEmail(e.target.value)
                      }
                      placeholder="your.email@school.edu"
                      required
                      disabled={resetLoading}
                      autoComplete="email"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={resetLoading}
                    className={styles["submit-btn"]}
                  >
                    {resetLoading ? "Sending…" : "Send reset link"}
                  </button>
                </form>
              )}

              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className={styles["text-btn"]}
                disabled={resetLoading}
              >
                Back to login
              </button>
            </div>
          ) : (
            <form onSubmit={handleEmailSignIn} className={styles["login-form"]}>
              <div className={styles["form-group"]}>
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmail(e.target.value)
                  }
                  placeholder="your.email@school.edu"
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className={styles["form-group"]}>
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(e.target.value)
                  }
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={styles["submit-btn"]}
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>

              <button
                type="button"
                onClick={openForgotPassword}
                className={styles["text-btn"]}
                disabled={loading}
              >
                Forgot Password?
              </button>
            </form>
          )}

          <div className={styles["login-note"]}>
            <p>
              <small>
                First time logging in? Use the password provided by your
                administrator or teacher.
              </small>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
